import React, { useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil } from "lucide-react";
import { useApp } from "../context/AppContext";
import { Endpoints } from "../lib/api";
import { PRIORITY_OPTIONS } from "../lib/time";
import { PageHeader } from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Dialog } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Select } from "../components/ui/select";
import { Badge } from "../components/ui/badge";

const COLORS = ["#0A84FF", "#5E5CE6", "#FF9F0A", "#30D158", "#64D2FF", "#FF375F", "#BF5AF2"];
const PRIORITY_TEXT = Object.fromEntries(PRIORITY_OPTIONS.map((o) => [Number(o.value), o.label]));

export default function WorkstationsPage() {
  const { workstations, refreshLists } = useApp();
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const empty = { name: "", priority: 3, max_consecutive_duration: 4, color: COLORS[0] };
  const [form, setForm] = useState(empty);

  const openCreate = () => { setEditId(null); setForm(empty); setOpen(true); };
  const openEdit = (w) => {
    setEditId(w.id);
    setForm({ name: w.name, priority: w.priority, max_consecutive_duration: w.max_consecutive_duration, color: w.color });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    const body = {
      name: form.name.trim(),
      priority: Number(form.priority),
      max_consecutive_duration: Number(form.max_consecutive_duration),
      color: form.color,
    };
    try {
      if (editId) await Endpoints.updateWorkstation(editId, body);
      else await Endpoints.createWorkstation(body);
      await refreshLists();
      setOpen(false);
      toast.success(editId ? "Workstation updated" : "Workstation added", {
        description: "Add/adjust a requirement, then regenerate.",
      });
    } catch (e) {
      toast.error("Could not save workstation", { description: e?.response?.data?.detail || e.message });
    }
  };

  const remove = async (id) => {
    await Endpoints.deleteWorkstation(id);
    await refreshLists();
    toast.success("Workstation removed");
  };

  return (
    <div className="animate-fadeup">
      <PageHeader title="Workstations" subtitle="Duties/posts with a priority and rotation limit. Every workstation is open to any profession — restrict who can staff a slot on the Requirements page via 'Only these professions'.">
        <Button variant="primary" size="sm" onClick={openCreate} data-testid="add-workstation-btn">
          <Plus className="w-4 h-4" /> New workstation
        </Button>
      </PageHeader>

      <Card className="overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left border-b border-line text-[11px] uppercase tracking-wider text-muted">
              <Th>Workstation</Th><Th>Priority</Th><Th>Max consecutive</Th><Th></Th>
            </tr>
          </thead>
          <tbody>
            {workstations.map((w) => (
              <tr key={w.id} className="border-b border-line last:border-0 hover:bg-white/5 transition-colors duration-100" data-testid={`workstation-row-${w.id}`}>
                <Td>
                  <div className="flex items-center gap-2.5">
                    <span className="w-3 h-3 rounded-sm border border-black/40" style={{ backgroundColor: w.color }} />
                    <span className="text-ink font-medium">{w.name}</span>
                    {w.is_default && <Badge>Default</Badge>}
                  </div>
                </Td>
                <Td><Badge tone={w.priority}>{PRIORITY_TEXT[w.priority] || `P${w.priority}`}</Badge></Td>
                <Td className="mono text-muted">{w.max_consecutive_duration}h</Td>
                <Td className="text-right whitespace-nowrap">
                  {!w.is_default && (
                    <>
                      <Button variant="ghost" size="icon" onClick={() => openEdit(w)} data-testid={`edit-workstation-${w.id}`}>
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button variant="danger" size="icon" onClick={() => remove(w.id)} data-testid={`delete-workstation-${w.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </>
                  )}
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Dialog open={open} onClose={() => setOpen(false)} title={editId ? "Edit workstation" : "New workstation"} testid="workstation-dialog"
        footer={<><Button variant="ghost" size="sm" onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="primary" size="sm" onClick={save} data-testid="save-workstation-btn">{editId ? "Save changes" : "Add workstation"}</Button></>}>
        <Input label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="workstation-name-input" placeholder="e.g. Gate 22" />
        <div className="grid grid-cols-2 gap-3">
          <Select label="Priority" value={String(form.priority)} onChange={(v) => setForm({ ...form, priority: v })}
            options={PRIORITY_OPTIONS} testid="workstation-priority-select" />
          <Input label="Max consecutive (h)" type="number" min="0" step="0.25" value={form.max_consecutive_duration} onChange={(e) => setForm({ ...form, max_consecutive_duration: e.target.value })} data-testid="workstation-maxconsec-input" />
        </div>
        <div>
          <span className="block text-[11px] uppercase tracking-wider text-muted mb-1.5">Colour</span>
          <div className="flex gap-2">
            {COLORS.map((c) => (
              <button key={c} type="button" onClick={() => setForm({ ...form, color: c })}
                className={`w-7 h-7 rounded-sm border-2 transition-transform duration-150 ${form.color === c ? "border-white scale-110" : "border-transparent"}`}
                style={{ backgroundColor: c }} />
            ))}
          </div>
        </div>
      </Dialog>
    </div>
  );
}

const Th = ({ children }) => <th className="px-4 py-3 font-medium">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-4 py-3 ${className}`}>{children}</td>;
